import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminFetch } from "@/hooks/use-admin-fetch";

/**
 * Admin → Challenges (feature requests / ideas).
 *
 * Port of `src/app/admin/challenges/page.tsx` (legacy NextJS, ~200 LOC) per D21.
 *
 * Endpoints:
 *   - GET    /api/admin/challenges
 *   - PATCH  /api/admin/challenges (id, status?, adminResponse?)
 *   - DELETE /api/admin/challenges (id)
 */

interface Challenge {
  id: string;
  title: string;
  description: string;
  authorType: string;
  authorLabel: string | null;
  category: string;
  targetAudience: string;
  voteCount: number;
  commentCount: number;
  status: string;
  adminResponse: string | null;
  priority: number;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  open: "bg-gray-100 text-gray-600",
  in_progress: "bg-amber-100 text-amber-700",
  done: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-600",
};

export const Route = createFileRoute("/_authenticated/challenges")({
  component: ChallengesPage,
});

function ChallengesPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminFetch.get<Challenge[]>("/api/admin/challenges");
        if (!cancelled) setChallenges(data);
      } catch {
        if (!cancelled) setChallenges([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [adminFetch]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await adminFetch.patch("/api/admin/challenges", { id, status });
      setChallenges((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch {
      /* ignore */
    }
  };

  const sendResponse = async (id: string) => {
    if (!responseText.trim()) return;
    try {
      await adminFetch.patch("/api/admin/challenges", { id, adminResponse: responseText });
      setChallenges((prev) =>
        prev.map((c) => (c.id === id ? { ...c, adminResponse: responseText } : c)),
      );
      setRespondingId(null);
      setResponseText("");
    } catch {
      /* ignore */
    }
  };

  const deleteChallenge = async (id: string) => {
    if (!confirm(t`Delete this challenge?`)) return;
    try {
      await adminFetch.raw("/api/admin/challenges", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      setChallenges((prev) => prev.filter((c) => c.id !== id));
    } catch {
      /* ignore */
    }
  };

  const grouped = {
    open: challenges.filter((c) => c.status === "open"),
    in_progress: challenges.filter((c) => c.status === "in_progress"),
    done: challenges.filter((c) => c.status === "done"),
    rejected: challenges.filter((c) => c.status === "rejected"),
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Challenges</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Feature requests and ideas from users — {challenges.length} total</Trans>
        </p>
      </div>

      <div className="flex gap-3">
        <Badge variant="outline">
          <Trans>{grouped.open.length} Open</Trans>
        </Badge>
        <Badge className="bg-amber-100 text-amber-700">
          <Trans>{grouped.in_progress.length} In Progress</Trans>
        </Badge>
        <Badge className="bg-green-100 text-green-700">
          <Trans>{grouped.done.length} Done</Trans>
        </Badge>
        <Badge className="bg-red-100 text-red-600">
          <Trans>{grouped.rejected.length} Rejected</Trans>
        </Badge>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>Loading...</Trans>
        </div>
      ) : challenges.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>No challenges yet</Trans>
        </div>
      ) : (
        <div className="space-y-2">
          {challenges.map((c) => (
            <div key={c.id} className="rounded-xl border bg-white p-4 dark:bg-gray-900">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">
                      <Trans>{c.voteCount} votes</Trans>
                    </span>
                    <Badge className={`text-[0.625rem] ${statusColors[c.status] || ""}`}>
                      {c.status}
                    </Badge>
                    <Badge variant="outline" className="text-[0.625rem]">
                      {c.targetAudience}
                    </Badge>
                    <Badge variant="outline" className="text-[0.625rem]">
                      {c.category}
                    </Badge>
                  </div>
                  <h3 className="mt-1 font-semibold">{c.title}</h3>
                  <p className="text-muted-foreground mt-0.5 text-sm">{c.description}</p>
                  <p className="mt-1 text-[0.625rem] text-gray-400">
                    {c.authorLabel || c.authorType} · {c.commentCount} <Trans>comments</Trans> ·{" "}
                    {new Date(c.createdAt).toLocaleDateString()}
                  </p>
                  {c.adminResponse && (
                    <div className="bg-primary/5 mt-2 rounded px-3 py-1.5 text-xs">
                      <strong>
                        <Trans>Response:</Trans>
                      </strong>{" "}
                      {c.adminResponse}
                    </div>
                  )}
                  {respondingId === c.id && (
                    <div className="mt-2 flex gap-2">
                      <Input
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder={t`Write admin response...`}
                        className="text-xs"
                      />
                      <Button size="sm" onClick={() => sendResponse(c.id)}>
                        <Trans>Send</Trans>
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRespondingId(null)}>
                        <Trans>Cancel</Trans>
                      </Button>
                    </div>
                  )}
                </div>
                <div className="ml-3 flex shrink-0 flex-col gap-1">
                  <select
                    value={c.status}
                    onChange={(e) => updateStatus(c.id, e.target.value)}
                    className="rounded border px-2 py-1 text-xs"
                  >
                    <option value="open">{t`Open`}</option>
                    <option value="in_progress">{t`In Progress`}</option>
                    <option value="done">{t`Done`}</option>
                    <option value="rejected">{t`Rejected`}</option>
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs"
                    onClick={() => {
                      setRespondingId(c.id);
                      setResponseText(c.adminResponse || "");
                    }}
                  >
                    <Trans>Respond</Trans>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-red-500"
                    onClick={() => deleteChallenge(c.id)}
                  >
                    <Trans>Delete</Trans>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
